import {
	applySwitchFilter,
	focusSwitch,
	openHomeSwitchSection,
	openSiteSwitchSection,
	openGlobalVlansSection,
	openSwitchBulkSection,
	openSwitchDashboardSection,
} from './adapter';
import {
	buildSwitchDashboardPath,
	buildSwitchNavigationState,
	isSwitchNavigationReady,
} from './navigation';
import {
	openSwitchConfigureModal,
	openSwitchConfigurePortModal,
	openSwitchContinuousPoeModal,
	openSwitchCreateModal,
	openSwitchCreateVlanModal,
	openSwitchDeleteVlanModal,
	openSwitchDeleteModal,
	openSwitchFactoryResetModal,
	openSwitchMoveModal,
	openSwitchPartitionModal,
	openSwitchRebootModal,
} from './modal';
import {resolveSwitches} from './resolver';
import {serializeSwitchContext} from './context';
import {readSwitchStatusCounts} from './statistics';

/**
 * Switch.
 *
 * The device, not the profiles: switch_events and the three Switch ACL
 * collections are separate plugins and separate resources.
 *
 * Three surfaces, three shapes. On the Home Dashboard the inventory is an
 * accordion; on the Site Dashboard it is a tab that renders a whole sub-page
 * scoped to the site; and one switch has its own route, keyed by serial.
 * Every one of these opens a Canvas form or confirmation and stops there.
 * Nothing below reboots, wipes, moves or deletes a switch: the person
 * presses the button inside the dialog, or nothing happens.
 */
const switchPlugin = {
	id: 'switch',
	version: 1,
	commandVersion: 1,
	resource: 'switch',
	operations: [
		'options',
		'list',
		'search',
		'open',
		'create',
		'configure',
		'move',
		'reboot',
		'delete',
		'factory_reset',
		'partition',
		'continuous_poe',
		'ports',
		'configure_port',
		'port_statistics',
		'mac_table',
		'alarm_list',
		'lldp_neighbours',
		'global_vlans',
		'create_vlan',
		'delete_vlan',
		'bulk_add',
		'bulk_delete',
		'bulk_reboot',
		'bulk_update',
	],
	dependencies: {
		// A Site-scoped inventory looks the Site up first, with the Site
		// plugin's resolver - the same arrangement the Access Point uses.
		plugins: ['site', 'canvas.navigation'],
		resolvers: ['site'],
		destinations: [
			'home.dashboard',
			'site.dashboard',
			'maintenance.dashboard',
		],
	},
	destinations: {
		'switch.dashboard': {
			path: buildSwitchDashboardPath,
			buildState: buildSwitchNavigationState,
		},
	},
	completionVerifiers: {
		'navigate:switch.dashboard': isSwitchNavigationReady,
	},
	resolvers: {
		switch: resolveSwitches,
	},
	sections: {
		'home.switches': openHomeSwitchSection,
		'site.switches': openSiteSwitchSection,
		// The Switch Dashboard itself, not an accordion on it. Declared in
		// destinations.json beside its five accordions but never handled, so
		// anything reaching a switch through its own context - where the
		// destination is the dashboard and the section is the page you have
		// just arrived on - failed at the section and never got to its modal.
		// Nothing to open: arriving is the whole of it.
		'switch.overview': () => Promise.resolve(),
		// Five accordions on one Switch Dashboard, so one handler keyed by
		// which was asked for rather than five that differ only in a string.
		'switch.ports': () => openSwitchDashboardSection('switch.ports'),
		'switch.port_statistics': () =>
			openSwitchDashboardSection('switch.port_statistics'),
		'switch.mac_table': () =>
			openSwitchDashboardSection('switch.mac_table'),
		'switch.alarms': () => openSwitchDashboardSection('switch.alarms'),
		'switch.lldp': () => openSwitchDashboardSection('switch.lldp'),
		'site.global_vlans': openGlobalVlansSection,
		'maintenance.bulk_add_switches': () =>
			openSwitchBulkSection('maintenance.bulk_add_switches'),
		'maintenance.bulk_delete_switches': () =>
			openSwitchBulkSection('maintenance.bulk_delete_switches'),
		'maintenance.bulk_switch_actions': () =>
			openSwitchBulkSection('maintenance.bulk_switch_actions'),
	},
	filters: {
		switch: (command) => applySwitchFilter(command.params?.query),
	},
	focusHandlers: {
		switch: (command) => focusSwitch(command.params?.entity_id),
	},
	statistics: {
		switch_status: readSwitchStatusCounts,
	},
	modals: {
		'switch.create': openSwitchCreateModal,
		'switch.configure': openSwitchConfigureModal,
		'switch.move': openSwitchMoveModal,
		'switch.reboot': openSwitchRebootModal,
		'switch.delete': openSwitchDeleteModal,
		'switch.factory_reset': openSwitchFactoryResetModal,
		'switch.partition': openSwitchPartitionModal,
		'switch.continuous_poe': openSwitchContinuousPoeModal,
		'switch.configure_port': openSwitchConfigurePortModal,
		'switch.create_vlan': openSwitchCreateVlanModal,
		'switch.delete_vlan': openSwitchDeleteVlanModal,
	},
	contextSerializers: {
		switch: serializeSwitchContext,
	},
};

export default switchPlugin;
